import React, { useMemo, useState, useEffect, useEffectEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { getProduct } from "@/networking/products.api";
import { CartResponse } from "@/networking/carts.api";
import { Badge } from "@/components/fluid-ui/badge";
import { Button } from "../fluid-ui/button";
import { useCartMutations } from "./hooks/useCartMutations";
import { cn } from "@/lib/utils";

export default function VariantSelectionDropdown({
  productId,
  cartToken,
  paymentMethodsIncludes,
  onClose,
  itemId,
  quantity,
}: {
  productId?: number;
  cartToken: string;
  paymentMethodsIncludes: string | null;
  onClose: () => void;
  itemId: number;
  quantity: number;
}) {
  const { updateCartItemVariantMutation } = useCartMutations(
    cartToken,
    paymentMethodsIncludes,
  );
  const queryClient = useQueryClient();
  const cartQuery = queryClient.getQueryData<CartResponse>([
    "cart",
    cartToken,
    paymentMethodsIncludes,
  ]);
  const fluidShop = cartQuery?.cart?.company?.fluid_shop ?? "";
  const selectedVariantId =
    cartQuery?.cart?.items.find((item) => item.product?.id === productId)
      ?.variant?.id ?? null;

  const {
    data: product,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["product", productId, fluidShop],
    queryFn: () => getProduct(productId!, fluidShop),
    enabled: !!fluidShop && !!productId,
  });

  const optionGroups = useMemo(() => {
    if (!product?.variants) return {};

    const groups: Record<string, string[]> = {};

    product.variants.forEach((variant) => {
      variant.options.forEach((option) => {
        if (!groups[option.title]) {
          groups[option.title] = [];
        }
        const groupArray = groups[option.title];
        if (groupArray && !groupArray.includes(option.value)) {
          groupArray.push(option.value);
        }
      });
    });

    return groups;
  }, [product]);

  const initialSelectedOptions = useMemo(() => {
    if (!product?.variants || !selectedVariantId) return {};

    const currentVariant = product.variants.find(
      (v) => v.id === selectedVariantId,
    );
    if (!currentVariant) return {};

    const selected: Record<string, string> = {};
    currentVariant.options.forEach((option) => {
      selected[option.title] = option.value;
    });
    return selected;
  }, [product, selectedVariantId]);

  const [selectedOptions, setSelectedOptions] = useState<
    Record<string, string>
  >({});

  const handleSetSelectedOptionsEvent = useEffectEvent(
    (options: Record<string, string>) => {
      setSelectedOptions(options);
    },
  );

  useEffect(() => {
    if (Object.keys(initialSelectedOptions).length > 0) {
      handleSetSelectedOptionsEvent(initialSelectedOptions);
    }
  }, [initialSelectedOptions]);

  const handleOptionSelect = (optionTitle: string, optionValue: string) => {
    const newSelectedOptions = {
      ...selectedOptions,
      [optionTitle]: optionValue,
    };
    setSelectedOptions(newSelectedOptions);

    const matchingVariant = product?.variants.find((variant) => {
      return Object.entries(newSelectedOptions).every(([title, value]) => {
        return variant.options.some(
          (opt) => opt.title === title && opt.value === value,
        );
      });
    });

    if (matchingVariant && matchingVariant.id !== selectedVariantId) {
      updateCartItemVariantMutation.mutate({
        itemId: itemId,
        variantId: matchingVariant.id,
        quantity: quantity,
      });
    }
  };

  if (!fluidShop) {
    return null;
  }

  if (error) {
    console.error("Error fetching product variants:", error);
    return null;
  }

  if (isLoading || Object.entries(initialSelectedOptions).length === 0) {
    return (
      <div className="mt-2 w-full animate-pulse rounded bg-gray-100 p-4" />
    );
  }

  if (Object.entries(optionGroups).length === 0) {
    return null;
  }

  return (
    <div className="relative mt-2 w-full rounded bg-gray-100 p-3">
      <Button
        variant="ghost"
        onClick={onClose}
        className="absolute top-2 right-2 text-xs"
      >
        X
      </Button>
      {Object.entries(optionGroups).map(([optionTitle, values]) => (
        <div key={optionTitle} className="mb-2">
          <h3 className="text-sm font-semibold text-gray-500 capitalize">
            {optionTitle}
          </h3>
          <div className="flex flex-wrap gap-2">
            {values.map((value) => (
              <button
                key={`${optionTitle}-${value}`}
                onClick={() => handleOptionSelect(optionTitle, value)}
                disabled={updateCartItemVariantMutation.isPending}
              >
                <Badge
                  color={
                    selectedOptions[optionTitle] === value ? "black" : "white"
                  }
                  className={cn(
                    "h-auto max-w-full cursor-pointer rounded px-2 py-1 break-all capitalize",
                    {
                      "opacity-50": updateCartItemVariantMutation.isPending,
                    },
                  )}
                  size="sm"
                >
                  {value}
                </Badge>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
